import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  ArrowLeft, MapPin, Plug, RotateCcw, Copy, Globe, Lock, Search, PlayCircle, Store, Filter, EyeOff, BellRing, CalendarDays,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function StatBlok({ label, waarde, accent }) {
  return (
    <div className={`border rounded-lg p-3 ${accent ? "bg-primary/5 border-primary/20" : "bg-secondary/30 border-border"}`}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="tabular-nums font-semibold text-lg mt-0.5">{waarde ?? "—"}</div>
    </div>
  );
}

function StoreLocatorBeheer({ isAdmin }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [kanalen, setKanalen] = useState([]);
  const [gekozen, setGekozen] = useState(null); // null = nog niet geladen
  const [kanBusy, setKanBusy] = useState(false);
  const [puntZoek, setPuntZoek] = useState("");
  const [puntRes, setPuntRes] = useState([]);
  const [uitgezet, setUitgezet] = useState([]);
  const [puntTotaal, setPuntTotaal] = useState(null);
  const [puntBusy, setPuntBusy] = useState(false);
  const feedUrl = `${BACKEND}/api/public/stores`;

  const laad = async () => {
    try {
      const { data } = await api.get("/store-locator/status");
      setStatus(data);
    } catch { /* stil */ }
  };
  const laadKanalen = async () => {
    try {
      const { data } = await api.get("/store-locator/kanalen");
      setKanalen(data.beschikbaar || []);
      setGekozen(data.geselecteerd || []);
    } catch { /* stil */ }
  };
  useEffect(() => { laad(); laadKanalen(); laadPunten(""); }, []);

  // Poll tijdens een lopende backfill of H&B-import
  useEffect(() => {
    if (!status?.backfill?.running && !status?.hb?.running) return;
    const t = setInterval(laad, 3000);
    return () => clearInterval(t);
  }, [status?.backfill?.running, status?.hb?.running]);

  const startBackfill = async () => {
    setBusy(true);
    try {
      await api.post("/store-locator/geocode-backfill");
      toast.success("Geocoding gestart op de achtergrond");
      setTimeout(laad, 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Starten mislukt");
    } finally { setBusy(false); }
  };

  const startHb = async () => {
    setBusy(true);
    try {
      await api.post("/store-locator/scrape-hb");
      toast.success("Holland & Barrett-winkels worden opgehaald");
      setTimeout(laad, 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Starten mislukt");
    } finally { setBusy(false); }
  };

  const hb = status?.hb || {};
  const hbPct = hb.totaal ? Math.round(((hb.verwerkt || 0) / hb.totaal) * 100) : 0;

  const toggleKanaal = (waarde) => {
    setGekozen((g) => (g || []).includes(waarde) ? g.filter((w) => w !== waarde) : [...(g || []), waarde]);
  };
  const bewaarKanalen = async () => {
    setKanBusy(true);
    try {
      await api.put("/store-locator/kanalen", { kanalen: gekozen || [] });
      toast.success("Winkeltypes opgeslagen");
      laad();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    } finally { setKanBusy(false); }
  };

  const bf = status?.backfill || {};
  const pct = bf.totaal ? Math.round(((bf.verwerkt || 0) / bf.totaal) * 100) : 0;

  const laadPunten = async (q) => {
    try {
      const { data } = await api.get("/store-locator/punten", { params: { q: q || "" } });
      setPuntRes(data.zoekresultaat || []);
      setUitgezet(data.uitgezet || []);
      setPuntTotaal(data.totaal_punten ?? null);
    } catch { /* stil */ }
  };
  const zoekPunten = async (e) => {
    e?.preventDefault?.();
    await laadPunten(puntZoek);
  };
  const zetZichtbaar = async (punt, zichtbaar) => {
    setPuntBusy(true);
    try {
      await api.put(`/store-locator/punten/${punt.id}/zichtbaar`, { zichtbaar, bron: punt.bron });
      toast.success(zichtbaar ? `${punt.naam} staat weer op de kaart` : `${punt.naam} is van de kaart gehaald`);
      await laadPunten(puntZoek);
      laad();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wijzigen mislukt");
    } finally { setPuntBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="store-locator-card">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-primary/10 text-primary"><Store className="h-5 w-5" /></div>
        <div className="flex-1">
          <h2 className="font-display text-xl font-medium">Store-locator (nextbrush.nl)</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Publieke, alleen-lezen lijst met <strong>NextBrush-verkooppunten</strong> voor de consumentenwebsite.
            Een organisatie komt erop als zij zelf <strong>ooit</strong> NextBrush kocht, óf als haar
            klantcode-prefix overeenkomt met een groothandel die ooit NextBrush kocht (bv. Ekoplaza-filialen
            <em> UDEA-xxx</em> via Udea). Voorwaarde: een volledig adres en <em>"Tonen op nextbrush.nl"</em> niet
            uitgezet (ontbrekend = aan). Winkels zónder coördinaten komen er óók op (lat/lng null);
            coördinaten worden automatisch aangevuld (PDOK voor NL, OpenStreetMap buitenland). Plus externe
            ketens. Nooit e-mail, contactpersonen, omzet of prijzen.
          </p>
        </div>
      </div>

      {/* Feed-URL */}
      <div className="flex items-center gap-2 flex-wrap">
        <code className="font-mono text-xs bg-secondary px-2 py-1.5 rounded flex-1 min-w-[200px] break-all" data-testid="feed-url">{feedUrl}</code>
        <Button variant="outline" size="sm" data-testid="copy-feed-url"
          onClick={() => { navigator.clipboard?.writeText(feedUrl); toast.success("URL gekopieerd"); }}>
          <Copy className="h-3.5 w-3.5 mr-1" /> Kopieer
        </Button>
        <Button variant="outline" size="sm" asChild>
          <a href={feedUrl} target="_blank" rel="noreferrer"><Globe className="h-3.5 w-3.5 mr-1" /> Open</a>
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatBlok label="Op de kaart" waarde={status?.feed_totaal} accent />
        <StatBlok label="Klanten op de kaart" waarde={status?.klanten_op_de_kaart} />
        <StatBlok label="Met coördinaten" waarde={status?.klanten_met_coordinaten} />
        <StatBlok label="Externe punten (H&B)" waarde={status?.externe_verkooppunten} />
      </div>

      {/* NextBrush-uitsplitsing + uitsluitingen */}
      {status?.nextbrush && (
        <div className="border border-border rounded-lg p-4 space-y-2" data-testid="nextbrush-telling">
          <div className="text-sm font-medium">NextBrush-verkooppunten (ooit gekocht)</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <StatBlok label="Kocht zelf (direct)" waarde={status.nextbrush.direct} />
            <StatBlok label="Via groothandel" waarde={status.nextbrush.via_groothandel} />
            <StatBlok label="Nog zonder coördinaten" waarde={status.nextbrush.zonder_coordinaten} />
            <StatBlok label="Uitgesloten — geen adres" waarde={status.nextbrush.uitgesloten_geen_adres} />
            <StatBlok label="Uitgesloten — uitgezet" waarde={status.nextbrush.uitgesloten_uitgezet} />
            <StatBlok label="Uitgefilterd op winkeltype" waarde={status.nextbrush.uitgefilterd_kanaal} />
          </div>
        </div>
      )}

      {/* Winkeltypes op de kaart (instelbaar) */}
      <div className="border border-border rounded-lg p-4 space-y-3" data-testid="kanaal-filter">
        <div className="flex items-start gap-2">
          <Filter className="h-4 w-4 text-primary mt-0.5" />
          <div>
            <div className="font-medium text-sm">Optioneel extra filter: winkeltype</div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Standaard komen álle NextBrush-verkooppunten op de kaart. Wil je beperken tot bepaalde
              winkeltypes (kenmerk <em>Kanaal</em>, bv. Drogist of Bio), vink die dan hier aan. Niets
              aangevinkt = geen beperking.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
          {kanalen.map((k) => {
            const aan = (gekozen || []).includes(k.waarde);
            return (
              <label key={k.waarde} className="flex items-center gap-2 text-sm cursor-pointer select-none"
                data-testid={`kanaal-${k.waarde}`}>
                <Checkbox checked={aan} disabled={!isAdmin} onCheckedChange={() => toggleKanaal(k.waarde)} />
                <span className="flex-1">{k.waarde}</span>
                <span className="text-xs text-muted-foreground tabular-nums">{k.aantal}</span>
              </label>
            );
          })}
          {kanalen.length === 0 && <div className="text-xs text-muted-foreground">Geen winkeltypes gevonden.</div>}
        </div>
        {isAdmin && (
          <div className="flex items-center gap-3 pt-1">
            <Button size="sm" onClick={bewaarKanalen} disabled={kanBusy || gekozen === null} data-testid="kanaal-opslaan">
              {kanBusy ? "Opslaan…" : "Opslaan"}
            </Button>
            <span className="text-xs text-muted-foreground">{(gekozen || []).length} type(s) geselecteerd</span>
          </div>
        )}
      </div>

      {/* Individuele punten aan/uit (ongeacht winkeltype) */}
      <div className="border border-border rounded-lg p-4 space-y-3" data-testid="punten-beheer">
        <div className="flex items-start gap-2">
          <EyeOff className="h-4 w-4 text-primary mt-0.5" />
          <div>
            <div className="font-medium text-sm">Punten individueel aan/uit</div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Zet een specifiek verkooppunt van de kaart, ongeacht het winkeltype of de kenmerken.
              Zoek op naam of plaats en gebruik de schakelaar.{puntTotaal != null ? ` (${puntTotaal} punten totaal)` : ""}
            </p>
          </div>
        </div>
        <form onSubmit={zoekPunten} className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={puntZoek} onChange={(e) => setPuntZoek(e.target.value)}
              placeholder="Zoek winkel of plaats…" className="pl-8" data-testid="punten-zoek-input" disabled={!isAdmin} />
          </div>
          <Button type="submit" size="sm" variant="outline" disabled={!isAdmin} data-testid="punten-zoek-btn">Zoek</Button>
        </form>

        {puntRes.length > 0 && (
          <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="punten-zoekresultaat">
            {puntRes.map((p) => (
              <div key={`${p.bron}-${p.id}`} className="flex items-center gap-3 rounded-lg border border-border/60 bg-secondary/20 px-3 py-2 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{p.naam}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {[p.plaats, p.land].filter(Boolean).join(", ")}
                    {p.reden === "geen_adres" ? " · geen adres" : ""}
                  </div>
                </div>
                <Badge variant="outline" className="shrink-0 text-[10px]">
                  {p.bron === "extern" ? (p.chain || "Extern") : (p.soort === "groothandel" ? "Via groothandel" : "Direct")}
                </Badge>
                <Switch checked={p.zichtbaar} disabled={!isAdmin || puntBusy}
                  onCheckedChange={(v) => zetZichtbaar(p, v)}
                  data-testid={`punt-switch-${p.id}`} />
              </div>
            ))}
          </div>
        )}
        {puntZoek && puntRes.length === 0 && (
          <div className="text-xs text-muted-foreground">Geen punten gevonden voor "{puntZoek}".</div>
        )}

        <div className="pt-1">
          <div className="text-xs font-medium text-muted-foreground mb-1">Nu van de kaart gehaald ({uitgezet.length})</div>
          {uitgezet.length === 0 ? (
            <div className="text-xs text-muted-foreground">Geen punten handmatig uitgezet.</div>
          ) : (
            <div className="space-y-1.5 max-h-60 overflow-y-auto" data-testid="punten-uitgezet">
              {uitgezet.map((p) => (
                <div key={`${p.bron}-${p.id}`} className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{p.naam}</div>
                    <div className="text-xs text-muted-foreground truncate">{[p.plaats, p.land].filter(Boolean).join(", ")}</div>
                  </div>
                  <Badge variant="outline" className="shrink-0 text-[10px]">
                    {p.bron === "extern" ? (p.chain || "Extern") : (p.soort === "groothandel" ? "Via groothandel" : "Direct")}
                  </Badge>
                  <Switch checked={false} disabled={!isAdmin || puntBusy}
                    onCheckedChange={() => zetZichtbaar(p, true)}
                    data-testid={`punt-aan-${p.id}`} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {isAdmin && (
        <div className="flex flex-wrap items-center gap-3 border-t border-border pt-3">
          <Button onClick={startBackfill} disabled={busy || bf.running} data-testid="backfill-btn">
            <PlayCircle className="h-4 w-4 mr-2" />
            {bf.running ? "Bezig met geocoderen…" : "Geocode ontbrekende adressen"}
          </Button>
          <Button variant="ghost" size="sm" onClick={laad} data-testid="status-ververs">
            <RotateCcw className="h-4 w-4 mr-1" /> Ververs
          </Button>
          {status && (
            <span className="text-xs text-muted-foreground">
              {status.klanten_zonder_coordinaten} klant(en) nog zonder coördinaten
            </span>
          )}
        </div>
      )}

      {/* Backfill-voortgang / resultaat */}
      {bf && (bf.running || bf.verwerkt != null) && (
        <div className="border border-border rounded-lg p-3 space-y-2" data-testid="backfill-status">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">
              {bf.running ? "Geocoderen…" : "Laatste backfill voltooid"}
            </span>
            <span className="tabular-nums">{bf.verwerkt || 0} / {bf.totaal || 0} · {bf.gelukt || 0} gelukt · {bf.mislukt || 0} mislukt</span>
          </div>
          <div className="h-2 bg-secondary rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
          {!bf.running && bf.mislukt > 0 && (bf.mislukt_lijst || []).length > 0 && (
            <details className="text-xs mt-1" data-testid="backfill-mislukt">
              <summary className="cursor-pointer text-muted-foreground">
                {bf.mislukt} adres(sen) konden niet gegeocodeerd worden — bekijk
              </summary>
              <div className="mt-2 max-h-48 overflow-y-auto border border-border rounded">
                {(bf.mislukt_lijst || []).map((m, i) => (
                  <div key={m.id || i} className="px-2 py-1 border-b border-border last:border-0">
                    <span className="font-medium">{m.naam}</span>
                    <span className="text-muted-foreground"> — {m.adres}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
      {/* Externe ketens: Holland & Barrett */}
      <div className="border border-border rounded-lg p-4 space-y-3" data-testid="hb-block">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="font-medium text-sm flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> Holland &amp; Barrett-winkels</div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Haalt alle NL-winkels van hollandandbarrett.nl op (adres uit de winkelpagina's) en zet ze
              als externe verkooppunten op de kaart. Idempotent — opnieuw draaien ververst de lijst.
            </p>
          </div>
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={startHb} disabled={busy || hb.running} data-testid="hb-import-btn">
              <MapPin className="h-3.5 w-3.5 mr-1" />
              {hb.running ? "Bezig…" : "Importeer / ververs"}
            </Button>
          )}
        </div>
        {hb && (hb.running || hb.verwerkt != null) && (
          <div className="space-y-1.5" data-testid="hb-status">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{hb.running ? "Ophalen…" : "Laatste import voltooid"}</span>
              <span className="tabular-nums">{hb.verwerkt || 0} / {hb.totaal || 0} · {hb.opgeslagen || 0} opgeslagen{hb.zonder_coord ? ` · ${hb.zonder_coord} zonder coörd.` : ""}</span>
            </div>
            <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${hbPct}%` }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AppleHerinneringenBeheer({ isAdmin }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [opschoonBusy, setOpschoonBusy] = useState(false);
  const laad = async () => {
    try { const { data } = await api.get("/reminders/config"); setCfg(data); } catch { setCfg(null); }
  };
  useEffect(() => { if (isAdmin) laad(); }, [isAdmin]);

  const kopieer = async (tekst, label) => {
    try { await navigator.clipboard.writeText(tekst || ""); toast.success(`${label} gekopieerd`); }
    catch { toast.error("Kopiëren mislukt"); }
  };
  const vernieuw = async () => {
    if (!window.confirm("Nieuwe sleutel maken? De oude links (kalender én snelkoppeling) werken daarna niet meer en moet je opnieuw instellen.")) return;
    setBusy(true);
    try { await api.post("/reminders/token/vernieuw"); await laad(); toast.success("Nieuwe sleutel gemaakt"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Mislukt"); }
    finally { setBusy(false); }
  };
  const opschonen = async () => {
    if (!window.confirm("Alle eerder door het CRM toegevoegde taken uit de iCloud-lijst 'CRM beltaken' verwijderen?")) return;
    setOpschoonBusy(true);
    try { await api.post("/icloud/opschonen"); toast.success("Opruimen gestart — dit kan een paar minuten duren op de achtergrond"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Opschonen mislukt"); }
    finally { setOpschoonBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-5" data-testid="reminders-card">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-primary/10 text-primary"><BellRing className="h-5 w-5" /></div>
        <div className="flex-1">
          <h2 className="font-display text-xl font-medium">Beltaken op je iPhone</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Je open beltaken en geplande verkoopkansen op je telefoon. Kies de makkelijke kalender-manier, of de snelkoppeling voor de Herinneringen-app.
          </p>
        </div>
        <Badge variant="outline" className="text-sky-700 border-sky-300 shrink-0" data-testid="reminders-badge">iPhone</Badge>
      </div>

      {!isAdmin && <div className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-4">Alleen beheerders kunnen de koppeling instellen.</div>}

      {isAdmin && (
        <>
          {/* Optie A — Kalender-abonnement */}
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-4 space-y-3" data-testid="reminders-agenda-blok">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-emerald-700" />
              <span className="font-medium text-sm">Optie 1 — Agenda-app (makkelijkst, 1 tik)</span>
              <Badge variant="outline" className="text-emerald-700 border-emerald-300 text-[10px]">aanbevolen</Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Open deze pagina <strong>op je iPhone</strong> en tik op de knop. Bevestig "Abonneren". Je taken staan daarna in de <strong>Agenda</strong>-app
              en worden automatisch bijgewerkt op al je Apple-apparaten. (Alleen-lezen — afvinken doe je in het CRM.)
            </p>
            <div className="flex gap-2 flex-wrap items-center">
              <Button asChild size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="reminders-webcal-btn">
                <a href={cfg?.webcal_url || "#"}><CalendarDays className="h-4 w-4 mr-1" />Abonneer in Agenda</a>
              </Button>
              <Button variant="outline" size="sm" onClick={() => kopieer(cfg?.ics_url, "Agenda-link")} data-testid="reminders-ics-copy-btn"><Copy className="h-4 w-4 mr-1" />Kopieer link</Button>
            </div>
            <p className="text-[11px] text-muted-foreground">Werkt de knop niet op desktop? Dat is normaal — gebruik 'm op je iPhone, of kopieer de link en open die op je telefoon.</p>
          </div>

          {/* Optie B — Snelkoppeling naar Herinneringen */}
          <div className="rounded-lg border border-border p-4 space-y-3" data-testid="reminders-shortcut-blok">
            <div className="flex items-center gap-2">
              <BellRing className="h-4 w-4 text-sky-700" />
              <span className="font-medium text-sm">Optie 2 — Herinneringen-app (snelkoppeling)</span>
            </div>
            <p className="text-xs text-muted-foreground">Eenmalig een korte snelkoppeling maken. Zo komen je taken écht in de Herinneringen-app.</p>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">Jouw persoonlijke link (geheim)</div>
              <div className="flex gap-2 flex-wrap items-center">
                <code className="font-mono text-xs bg-secondary px-2 py-1.5 rounded flex-1 min-w-[220px] break-all" data-testid="reminders-feed-url">{cfg?.feed_titels_url || "…"}</code>
                <Button variant="outline" size="sm" onClick={() => kopieer(cfg?.feed_titels_url, "Link")} data-testid="reminders-copy-btn"><Copy className="h-4 w-4 mr-1" />Kopiëren</Button>
                <Button variant="ghost" size="sm" onClick={vernieuw} disabled={busy} data-testid="reminders-token-btn"><RotateCcw className="h-4 w-4 mr-1" />Nieuwe sleutel</Button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">Deel deze link met niemand. Bij een nieuwe sleutel werken de oude links niet meer.</p>
            </div>

            <details className="rounded-lg border border-border bg-background" data-testid="reminders-instructies">
              <summary className="cursor-pointer px-4 py-3 text-sm font-medium select-none">Snelkoppeling maken — stap voor stap</summary>
              <div className="px-4 pb-4 pt-1 text-sm text-muted-foreground space-y-3">
                <p className="text-[11px] bg-secondary/60 rounded-md px-2 py-1.5">Werkt op iPhone/iPad met iOS 15 t/m de nieuwste versie (iOS 26/27). De namen tussen haakjes zijn de Engelse namen — handig als je iPhone op Engels staat of de vertaling iets afwijkt. Zoek de actie anders op het Engelse woord.</p>
                <ol className="list-decimal ml-5 space-y-1.5">
                  <li>Maak in de <strong>Herinneringen</strong>-app eenmalig een lijst met de naam <strong>CRM beltaken</strong>.</li>
                  <li>Open de app <strong>Opdrachten</strong> (Shortcuts) → tik rechtsboven op <strong>+</strong> → <strong>Actie toevoegen</strong> (Add Action).</li>
                  <li>Zoek <strong>"Inhoud van URL ophalen"</strong> <span className="text-muted-foreground">(Get Contents of URL)</span>, voeg toe en plak bij de URL de link hierboven (knop Kopiëren).</li>
                  <li>Zoek en voeg toe <strong>"Herhaal met elk onderdeel"</strong> <span className="text-muted-foreground">(Repeat with Each)</span> en kies als invoer <em>Inhoud van URL</em>. Je krijgt nu een blok met onderaan <strong>"Stop herhalen"</strong>.</li>
                  <li><strong>Binnen</strong> dat herhaal-blok (dus tussen "Herhaal met elk onderdeel" en "Stop herhalen"): voeg <strong>"Nieuwe herinnering"</strong> <span className="text-muted-foreground">(Add New Reminder)</span> toe. Staat de actie er per ongeluk boven/buiten? Sleep 'm dan naar binnen — anders werkt stap hieronder niet.</li>
                  <li>De actie toont standaard de tekst <em>'Herinnering'</em> als titel — <strong>tik daarop</strong> en kies via de variabelenkiezer <strong>Herhaal-onderdeel</strong> (Repeat Item). Zet <em>Lijst</em> op <strong>CRM beltaken</strong>. Bewaar en tik op de opdracht om te draaien.</li>
                </ol>
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  <strong>Staat "Herhaal-onderdeel" (Repeat Item) niet in de variabelenlijst?</strong> Dan staat de actie <em>Nieuwe herinnering</em> buiten de herhaal-lus. Sleep 'm zodat hij <strong>tussen</strong> "Herhaal met elk onderdeel" en "Stop herhalen" staat (ingesprongen). Pas dán verschijnt Herhaal-onderdeel.<br />
                  <strong>Krijg je herinneringen met de titel "Herinnering"?</strong> Dan staat de titel nog op de standaardtekst — tik erop en kies de variabele Herhaal-onderdeel. Tip: de makkelijkste optie hierboven (kalender-abonnement) heeft dit gedoe helemaal niet.
                </div>
                <div className="rounded-md bg-secondary/60 p-3">
                  <div className="font-medium text-foreground text-xs">Automatisch laten draaien (optioneel)</div>
                  <p className="text-xs mt-1">Tabblad <strong>Automatisering</strong> (Automation) → <strong>+</strong> → <strong>Tijdstip van de dag</strong> (Time of Day) → kies deze opdracht → "Direct uitvoeren" (Run Immediately) aan.</p>
                </div>
                <details className="rounded-md border border-border">
                  <summary className="cursor-pointer px-3 py-2 text-xs font-medium select-none">Geavanceerd: ook afvinken terugsturen naar het CRM (twee-weg)</summary>
                  <div className="px-3 pb-3 pt-1 text-xs space-y-1.5">
                    <p>Gebruik dan de rijke link <code className="bg-secondary px-1 rounded break-all">{cfg?.feed_url || ""}</code> (die bevat per taak een afvink-link in de notities).</p>
                    <p>Voeg vóór het ophalen deze stappen toe: <strong>Herinneringen zoeken</strong> <span className="text-muted-foreground">(Find Reminders)</span> (lijst CRM beltaken, Voltooid = Ja) → <strong>Herhaal</strong> (Repeat with Each) → <strong>URL's ophalen uit</strong> <span className="text-muted-foreground">(Get URLs from Input)</span> de notities → <strong>Inhoud van URL ophalen</strong> (Get Contents of URL) (dit vinkt af in het CRM). Leeg daarna de lijst met <strong>Verwijder herinneringen</strong> <span className="text-muted-foreground">(Delete Reminders)</span> en bouw 'm opnieuw op met de stappen hierboven. Kies dan als Titel én Notities de waarden <code className="bg-secondary px-1 rounded">titel</code> en <code className="bg-secondary px-1 rounded">notities</code> uit het onderdeel via <strong>Woordenlijstwaarde ophalen</strong> <span className="text-muted-foreground">(Get Dictionary Value)</span>.</p>
                  </div>
                </details>
              </div>
            </details>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border p-3 gap-3 flex-wrap">
            <div>
              <div className="font-medium text-sm">Oude iCloud-taken opruimen</div>
              <div className="text-xs text-muted-foreground">We hadden eerder taken rechtstreeks naar iCloud gepusht. Verwijder die hiermee.</div>
            </div>
            <Button variant="outline" size="sm" onClick={opschonen} disabled={opschoonBusy} data-testid="reminders-opschoon-btn">
              {opschoonBusy ? "Opschonen…" : "iCloud opruimen"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}


function UitgelichtKaart({ k }) {
  const kleur = { GET: "text-emerald-700 bg-emerald-50 border-emerald-200",
                  POST: "text-sky-700 bg-sky-50 border-sky-200",
                  PUT: "text-amber-700 bg-amber-50 border-amber-200",
                  PATCH: "text-amber-700 bg-amber-50 border-amber-200",
                  DELETE: "text-rose-700 bg-rose-50 border-rose-200" }[k.methode] || "";
  return (
    <div className="border border-border rounded-xl p-4 space-y-2" data-testid={`uitgelicht-${k.pad}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded border ${kleur}`}>{k.methode}</span>
        <code className="font-mono text-xs">{k.pad}</code>
      </div>
      <div className="font-medium text-sm">{k.titel}</div>
      <p className="text-xs text-muted-foreground">{k.beschrijving}</p>
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        {k.auth?.toLowerCase().startsWith("publiek") ? <Globe className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
        {k.auth}
      </div>
      {k.voorbeeld && (
        <pre className="bg-secondary/60 rounded-lg p-2.5 text-[11px] font-mono overflow-x-auto whitespace-pre-wrap">{k.voorbeeld}</pre>
      )}
    </div>
  );
}

export default function InstellingenApiKoppelingen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.rol === "admin";
  const [index, setIndex] = useState(null);
  const [zoek, setZoek] = useState("");
  const [busy, setBusy] = useState(false);

  const laad = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/onderhoud/api-index");
      setIndex(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "API-overzicht laden mislukt");
    } finally { setBusy(false); }
  };
  useEffect(() => { laad(); }, []);

  const groepenGefilterd = useMemo(() => {
    if (!index?.groepen) return [];
    const q = zoek.trim().toLowerCase();
    if (!q) return index.groepen;
    return index.groepen
      .map((g) => ({ ...g, endpoints: g.endpoints.filter((e) =>
        e.pad.toLowerCase().includes(q) || (e.samenvatting || "").toLowerCase().includes(q) || g.naam.toLowerCase().includes(q)) }))
      .filter((g) => g.endpoints.length > 0);
  }, [index, zoek]);

  const kleurMethode = (m) => ({ GET: "text-emerald-700", POST: "text-sky-700", PUT: "text-amber-700",
    PATCH: "text-amber-700", DELETE: "text-rose-700" }[m] || "text-foreground");

  return (
    <div className="space-y-6 max-w-4xl" data-testid="api-koppelingen-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Terug
        </Button>
      </div>

      <div>
        <div className="text-sm text-muted-foreground">Instellingen</div>
        <h1 className="font-display text-4xl font-medium tracking-tight mt-1">API's & koppelingen</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Alles om externe websites aan het CRM te koppelen. De endpoint-lijst wordt live uit de applicatie
          opgebouwd, dus nieuwe functionaliteit verschijnt hier automatisch.
        </p>
      </div>

      <StoreLocatorBeheer isAdmin={isAdmin} />

      <AppleHerinneringenBeheer isAdmin={isAdmin} />

      {/* Uitgelichte koppel-API's */}
      <div className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="uitgelicht-card">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-primary/10 text-primary"><Plug className="h-5 w-5" /></div>
          <div className="flex-1">
            <h2 className="font-display text-xl font-medium">Uitgelichte koppel-API's</h2>
            <p className="text-sm text-muted-foreground mt-1">De meest gebruikte endpoints voor website-integraties, met voorbeelden.</p>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {(index?.uitgelicht || []).map((k) => <UitgelichtKaart key={k.pad + k.methode} k={k} />)}
        </div>
      </div>

      {/* Volledige endpoint-lijst */}
      <div className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="alle-endpoints-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-primary/10 text-primary"><Globe className="h-5 w-5" /></div>
            <div>
              <h2 className="font-display text-xl font-medium">Alle beschikbare endpoints</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {index ? <>{index.totaal_endpoints} endpoints in {index.aantal_groepen} groepen · live opgebouwd {new Date(index.gegenereerd_op).toLocaleString("nl-NL")}</> : "Laden…"}
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={laad} disabled={busy} data-testid="api-ververs">
            <RotateCcw className="h-4 w-4 mr-1" /> Verversen
          </Button>
        </div>

        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder="Zoek op pad of omschrijving…"
            className="pl-9" data-testid="api-zoek" />
        </div>

        <div className="space-y-4">
          {groepenGefilterd.map((g) => (
            <div key={g.naam} data-testid={`api-groep-${g.naam}`}>
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                /{g.naam} <span className="text-muted-foreground/60">({g.endpoints.length})</span>
              </div>
              <div className="border border-border rounded-lg divide-y divide-border overflow-hidden">
                {g.endpoints.map((e) => (
                  <div key={e.methode + e.pad} className="flex items-start gap-3 px-3 py-2 text-sm">
                    <span className={`font-mono text-[11px] font-bold w-14 shrink-0 ${kleurMethode(e.methode)}`}>{e.methode}</span>
                    <div className="min-w-0 flex-1">
                      <code className="font-mono text-xs break-all">{e.pad}</code>
                      {e.samenvatting && <div className="text-xs text-muted-foreground">{e.samenvatting}</div>}
                    </div>
                    {e.publiek && <Badge variant="outline" className="shrink-0 text-[10px] gap-1"><Globe className="h-2.5 w-2.5" /> publiek</Badge>}
                  </div>
                ))}
              </div>
            </div>
          ))}
          {groepenGefilterd.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-6">Geen endpoints gevonden voor "{zoek}".</div>
          )}
        </div>
      </div>
    </div>
  );
}
